import {
  action, computed, makeAutoObservable, observable, observe, runInAction, when,
} from 'mobx';
import { getSessionStorageItem } from '@gamiphy/utils/storage';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { CoreStore } from '@gamiphy/loyalty-station-desktop/store/core';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { ReferralsStore } from '@gamiphy/loyalty-station-desktop/store/refferals';
import { WelcomeGiftStore } from '@gamiphy/loyalty-station-desktop/store/welcomeGift';
import {
  PopupConditionsDtoFieldEnum,
  PopupConditionsDtoOperationEnum,
  PopupDto,
  PopupDtoPredefinedTypeEnum,
  PopupsApi,
  PopupTriggerDetailsDtoTriggerEnum,
} from '@coretava/api/core-service';
import { shouldShowPopup } from './utils';
import { DiscountsStore } from '../discounts';
import { CashbackPopupsStore } from '../cashbackPopups';

class PopupsStore {
  @observable initialized = false;

  @observable popups: PopupDto[] = [];

  @observable opened = false;

  @observable showJoinProgram = false;

  constructor() {
    makeAutoObservable(this);
    observe(CoreStore, 'initialized', () => {
      when(() => !UiStore.opened && !!this.activePopup, () => {
        if (this.shouldAutoOpen) {
          this.checkFields();
        }
      });
    });
    observe(this, 'activePopup', () => {
      this.closePopup();
      when(() => !UiStore.opened && !!this.activePopup, () => {
        if (this.shouldAutoOpen) {
          this.checkFields();
        }
      });
    });
    observe(AppStore, 'initialized', () => {
      when(() => (AppStore.initialized), () => {
        void this.initialize();
      });
    });
    observe(UiStore, 'opened', () => {
      if (UiStore.opened) {
        this.closePopup();
      }
    });
  }

  @computed
  get api(): PopupsApi {
    return new PopupsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  public get activePopups(): PopupDto[] {
    return this.popups.filter((popup) => popup.enabled && !getSessionStorageItem(`popup_${popup.id}`));
  }

  @computed
  public get activePopup(): PopupDto | undefined {
    /**
     * Filtering the popups to three main categories
     * First is the always show popup, which have no conditions
     * Second is the logged out popups, that should only be shown when the user is not logged
     * Third is the logged in popups, that should only be shown when the user is logged/Draft user
     */
    const alwaysShowPopups = this.activePopups.filter((popup) => popup.conditions?.length === 0);
    const logoutPopups = this.activePopups.filter((popup) => popup.conditions?.find((condition) => condition.operation === PopupConditionsDtoOperationEnum.NotExists));
    const logoutIds = logoutPopups.map((popup) => popup.id);
    const alwaysShowIds = alwaysShowPopups.map((popup) => popup.id);
    const loginPopups = this.activePopups.filter((popup) => !logoutIds.includes(popup.id))
      .filter((popup) => !alwaysShowIds.includes(popup.id));

    if (alwaysShowPopups.length) {
      return alwaysShowPopups.find((popup) => !getSessionStorageItem(`popup_${popup.id}`));
    }

    if (logoutPopups.length && !UserStore.isLoggedIn) {
      return logoutPopups.find((popup) => !getSessionStorageItem(`popup_${popup.id}`));
    }

    return loginPopups.find((popup) => !getSessionStorageItem(`popup_${popup.id}`));
  }

  @computed get shouldAutoOpen(): boolean {
    return !!(this.activePopup && this.activePopup.triggerDetails.trigger === PopupTriggerDetailsDtoTriggerEnum.AutoOpen);
  }

  @computed
  get shouldOpenOnExit(): boolean {
    return !!(this.activePopup && this.activePopup.triggerDetails.trigger === PopupTriggerDetailsDtoTriggerEnum.ExitIntent);
  }

  @computed
  get showBasedOnType(): boolean {
    switch (this.activePopup?.predefinedType) {
      case PopupDtoPredefinedTypeEnum.WelcomeDiscount:
        return !!this.activePopup.cta.discountCode || DiscountsStore.isWelcomeGiftDiscount;
      default:
        return true;
    }
  }

  @computed
  get open(): boolean {
    return this.opened &&
      this.showBasedOnType &&
      !CashbackPopupsStore.showCashbackPopups &&
      !UiStore.loyaltyPageOpen;
  }

  @action
  async initialize(): Promise<void> {
    const response = await this.api.popupControllerList(AppStore.app.id);

    runInAction(() => {
      this.popups = response.data.popups;
      this.initialized = true;
    });
  }

  @action
  checkFields(): void {
    const conditionFields = this.activePopup?.conditions?.map((condition) => condition.field);
    if (conditionFields?.includes(PopupConditionsDtoFieldEnum.ReferralEnabled)) {
      when(() => ReferralsStore.initialized, () => {
        this.openPopup();
      });
    } else if (conditionFields?.includes(PopupConditionsDtoFieldEnum.WelcomeGiftEarned)) {
      when(() => WelcomeGiftStore.initialized, () => {
        this.openPopup();
      });
    } else {
      this.openPopup();
    }
  }

  @action
  openPopup(): void {
    if (this.activePopup && shouldShowPopup(this.activePopup)) {
      this.opened = true;
      void this.api.popupControllerView(AppStore.app.id, this.activePopup.id);
    }
  }

  @action
  closePopup(): void {
    this.opened = false;
  }

  @action openJoinProgram(): void {
    this.showJoinProgram = true;
  }

  @action closeJoinProgram(): void {
    this.showJoinProgram = false;
  }
}

export default new PopupsStore();
